import { useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";

import Header from "./components/header/header.component";
import Footer from "./components/footer/footer.component";
import ProtectedRoute from "./routes/protected-route";

import HomePage from "./pages/home-page.page";
import StockListPage from "./pages/stock-list-page.page";
import ChatPage from "./pages/chat-page.page";
import NotFoundPage from "./pages/not-found.page";
import LandingPage from "./pages/landing-page.page";
import WatchlistPage from "./pages/watchlist-page.page";
import AcceptTermsPage from "./pages/legal/accept-terms.page";
import PrivacyPolicyPage from "./pages/legal/privacy-policy.page";
import TermsOfServicePage from "./pages/legal/terms-of-service.page";

import { syncUserWithBackend, useMe } from "./services/clerk.service";

const AppLayout = () => {
  const location = useLocation();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user: clerkUser } = useUser();
  const [synced, setSynced] = useState(false);
  const { me, loading: meLoading, refetch: refetchMe } = useMe(synced);

  const hideHeader =
    location.pathname === "/landing" ||
    location.pathname === "/accept-terms" ||
    location.pathname === "/terms-of-service" ||
    location.pathname === "/privacy-policy";

  // Sync Clerk user to your DB on sign-in, then mark as synced
  useEffect(() => {
    if (!isSignedIn || !clerkUser) {
      setSynced(false);
      return;
    }
    syncUserWithBackend(clerkUser, getToken).then(() => setSynced(true));
  }, [isSignedIn, clerkUser, getToken]);

  // Wait for Clerk to load, sync to complete, and /me to return
  if (!isLoaded || (isSignedIn && (!synced || meLoading))) return null;

  // Redirect signed-in users away from landing
  if (isSignedIn && location.pathname === "/landing") {
    return <Navigate to="/" replace />;
  }

  // Redirect to accept-terms if user hasn't accepted yet
  if (isSignedIn && me && !me.tos_accepted_at) {
    if (location.pathname !== "/accept-terms") {
      return <Navigate to="/accept-terms" replace />;
    }
  }

  return (
    <>
      {!hideHeader && <Header />}
      <Routes>
        {/* Public */}
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* Accept terms — protected but exempt from tos redirect */}
        <Route
          path="/accept-terms"
          element={
            <ProtectedRoute>
              <AcceptTermsPage onAccepted={refetchMe} />
            </ProtectedRoute>
          }
        />

        {/* Protected */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock-list"
          element={
            <ProtectedRoute>
              <StockListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        {/* Old ticker-search URLs redirect to chat */}
        <Route path="/stock-search" element={<Navigate to="/chat" replace />} />
        <Route path="/stock-search/:ticker" element={<Navigate to="/chat" replace />} />
        <Route
          path="/watchlist"
          element={
            <ProtectedRoute>
              <WatchlistPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

export default App;
